/* eslint max-len: ["error", 200] */

import * as React from 'react';

import { Route } from '@reverbdotcom/commons/dist/route_settings';
import { MParticlePageName } from '@reverbdotcom/commons/dist/elog/mparticle_tracker';
import MarketplaceRoute from './routes/marketplace';
import { BrandRoute } from './routes/brand';
import CSPIndexRoute from './routes/csp_index';
import SaleRoute from './routes/sale';
import {
  ItemShowRoute,
  Item2ShowRoute,
  ItemPreviewRoute,
  ItemPreviewConfirmRoute,
} from './routes/item_show';
import HandpickedRoute from './routes/handpicked';
import CollectionRoute from './routes/collection';
import EndedWatchedProductsRoute from './routes/ended_watched_products';
import RecentlyViewedRoute from './routes/recently_viewed';
import WatchedProductsRoute from './routes/watched_products';
import FeedCustomizationRoute from './routes/feed_customization';
import MyAuctionsRoute from './routes/my_auctions';
import HomepageRoute from './routes/homepage';
import OnboardingRoutes from './routes/progressive_onboarding';
import ConvertShopTypeRoute from './routes/convert_shop_type';
import { CmsRoutes } from './routes/cms_route';
import {
  CoreIdentityVerificationRoute,
  LPIdentityVerificationRoute,
} from './routes/identity_verification';
import HolidaySaleRoute from './routes/holiday_sale';
import { ShopRoutes } from './routes/shop';
import { PRICE_GUIDE_ROUTE } from './components/shared/url_helpers';
import ALL_CHECKOUT_ROUTES from './routes/checkout';
import TrackArticle from '@reverbdotcom/commons/dist/components/track_article_page_view';

/*
 * All routes for the ReverbRouter are defined here. These are exported
 * separately so they can be shared between the client and server side
 * Router.
 *
 * Note: All routes here are defined with a relative route to support international
 * prefix routing, which is defined below in the actual export
 *
 * Please add a key to allow React to do some fancy DOM diffing (and to prevent an error in console)
 */
const Routes = [
  <Route
    key="my/selling/bump-listing/:listingId"
    path="my/selling/bump-listing/:listingId"
    props={{
      clientSide: true,
      pageViewEvent: {
        pageName: MParticlePageName.MobileBump,
      },
    }}
    getComponent={(_location, cb) => {
      require.ensure(
        [],
        (require) => {
          cb(null, require('./components/bump/mobile_bump_container').default);
        },
        'mobile-bump-listing',
      );
    }}
  />,
  <Route
    key="feed"
    path="my/feed"
    props={{
      clientSide: true,
      pageViewEvent: {
        pageName: MParticlePageName.Feed,
      },
    }}
    getComponent={(_location, cb) => {
      require.ensure(
        [],
        (require) => {
          cb(null, require('./feed/feed_grid').default);
        },
        'feed',
      );
    }}
  />,
  <Route
    key="sell"
    path="sell"
    props={{
      pageViewEvent: {
        pageName: MParticlePageName.SellForm,
      },
    }}
  />,
  <Route
    key="sell/search"
    path="sell/search"
    props={{
      clientSide: true,
      pageViewEvent: {
        pageName: MParticlePageName.SellSearch,
      },
    }}
    getComponent={(_location, cb) => {
      require.ensure(
        [],
        require => {
          cb(null, require('./components/sell/sell_search').default);
        },
        'sell',
      );
    }}
  />,
  <Route
    key="sell_prefilled"
    path="sell_prefilled"
    props={{
      pageViewEvent: ({ location }) => ({
        pageName: MParticlePageName.SellForm,
        cspId: location.query.seed_id as string,
      }),
    }}
  />,
  <Route
    key="item/:slug/similar"
    path="item/:slug/similar"
    getComponent={(_location, cb) => {
      require.ensure(
        [],
        (require) => {
          cb(
            null,
            require('./components/discovery/similar_listings_grid').default,
          );
        },
        'similar-listings',
      );
    }}
  />,
  <Route
    key="my/selling/shipping_labels/new/:orderId"
    path="my/selling/shipping_labels/new/:orderId"
    getComponent={(_, cb) => {
      require.ensure(
        [],
        (require) => {
          cb(
            null,
            require('./components/discovery/shipping_labels/shipping_labels_form_container')
              .default,
          );
        },
        'shipping-labels',
      );
    }}
  />,
  <Route
    key="my/selling/shipping_labels/:shipmentId"
    path="my/selling/shipping_labels/:shipmentId"
    getComponent={(_, cb) => {
      require.ensure(
        [],
        (require) => {
          cb(
            null,
            require('./components/discovery/shipping_labels/shipping_rates_form')
              .default,
          );
        },
        'shipping-labels',
      );
    }}
  />,
  <Route
    key="my/selling/shipping_labels/:shipmentId/edit"
    path="my/selling/shipping_labels/:shipmentId/edit"
    getComponent={(_, cb) => {
      require.ensure(
        [],
        (require) => {
          cb(
            null,
            require('./components/discovery/shipping_labels/edit_shipment_container')
              .default,
          );
        },
        'shipping-labels',
      );
    }}
  />,
  <Route
    key="price-guide"
    // The route for this page matches both the search route
    // and the show route. The show route is not rendered
    // by React. As a result `clientSide` has to be set to
    // false until the show route can be distinguished from
    // the search route.
    props={{ clientSide: false }}
    path={PRICE_GUIDE_ROUTE}
    getComponent={(_location, cb) => {
      require.ensure(
        [],
        (require) => {
          cb(
            null,
            require('./components/price_guides/price_guide_page').default,
          );
        },
        'price-guide',
      );
    }}
  />,
  <Route
    key="p/:slug/"
    props={{
      clientSide: true,
    }}
    path="p/:slug(/:condition)"
    getComponent={(_location, cb) => {
      require.ensure(
        [],
        (require) => {
          cb(null, require('./components/csp2_layout').default);
        },
        'csp2',
      );
    }}
  />,
  <Route
    key="brand-manager"
    props={{ clientSide: true }}
    path="brand-manager"
    getComponent={(_location, cb) => {
      require.ensure(
        [],
        (require) => {
          cb(
            null,
            require('./components/brand-manager/pages/index').default,
          );
        },
        'brand_manager',
      );
    }}
  />,
  <Route
    key="brand-manager/:brand_slug"
    path="brand-manager/:brand_slug"
    props={{ clientSide: true }}
    getComponent={(_location, cb) => {
      require.ensure(
        [],
        (require) => {
          cb(
            null,
            require('./components/brand-manager/pages/csps/index').default,
          );
        },
        'brand_manager',
      );
    }}
  />,
  <Route
    key="brand-manager/:brand_slug/:csp_slug"
    props={{ clientSide: true }}
    path="brand-manager/:brand_slug/:csp_slug"
    getComponent={(_location, cb) => {
      require.ensure(
        [],
        (require) => {
          cb(
            null,
            require('./components/brand-manager/pages/csps/show').default,
          );
        },
        'brand_manager',
      );
    }}
  />,
  <Route
    key="page/:slug"
    path="page/:slug"
    props={{
      pageViewEvent: ({ params }) => ({
        pageName: MParticlePageName.Page,
        pageSlug: params.slug,
      }),
    }}
  />,
  <Route
    key="news"
    path="news"
    props={{
      pageViewEvent: ({ location }) => ({
        pageName: MParticlePageName.NewsHomepage,
        articleQuery: location.query.query,
      }),
    }}
  />,
  // /news/the-forgotten-guitars-of-jimi-hendrix/locale/ja
  // We have the optional /locale portion here because we
  // have a number of links in the wild that use this
  // routing patern.
  //
  // Our Rails router handles this case without a 404,
  // and renders the content in the specified "locale"
  //
  // This ensures that the React Router is at parity
  // https://sentry.io/organizations/reverb-llc/issues/1770671512/?project=1769470
  <Route
    key="news/:slug"
    path="news/:slug(/locale/:locale)"
    getComponent={(_location, cb) => {
      cb(null, TrackArticle);
    }}
  />,
];

Routes.push(
  ShopRoutes,
  CSPIndexRoute,
  HomepageRoute,
  MyAuctionsRoute,
  SaleRoute,
  ItemShowRoute,
  Item2ShowRoute,
  ItemPreviewRoute,
  ItemPreviewConfirmRoute,
  HandpickedRoute,
  BrandRoute,
  CollectionRoute,
  ...MarketplaceRoute,
  EndedWatchedProductsRoute,
  FeedCustomizationRoute,
  RecentlyViewedRoute,
  WatchedProductsRoute,
  HolidaySaleRoute,
  ConvertShopTypeRoute,
  CoreIdentityVerificationRoute,
  LPIdentityVerificationRoute,
  ...CmsRoutes,
  ...ALL_CHECKOUT_ROUTES,
);
Routes.push(...OnboardingRoutes);

export default Routes;



// WEBPACK FOOTER //
// /home/reverb/workspace/core.deploy.production/build/src/node_modules/@reverbdotcom/discovery-ui/src/discovery-routes.tsx